import { useMemo } from 'react';
import { TmdxAvatar } from '@ds';
import { ResourceAvatar } from '@ui';
import { useRouteContext } from '@lp/core/contexts/RouteContext';
import { CrewLogisticsResourceType, LogisticsLegResourceType, RouteLegTypeEnum } from '@/api/apis/route';
import { CaseResourceType } from '@/api/apis/case';
import { EmployeeComposed } from '@/api/apis/resources';
import { getFullName } from '@/utils/helpers';

export const getEmployeeName = (type: string, data?: Pick<EmployeeComposed, 'firstName' | 'lastName'>): string => {
  if (data) {
    return `${data.firstName} ${data.lastName} (${type})`;
  }

  return type;
};

type ResourcesLegInfoProps = {
  legType: RouteLegTypeEnum;
  resources: LogisticsLegResourceType[];
  hasScs: boolean;
};

export const ResourcesLegInfo = ({ legType, resources, hasScs }: ResourcesLegInfoProps) => {
  const { resourceById } = useRouteContext();

  const isAccommodationLeg = legType === RouteLegTypeEnum.AccommodationLeg;

  const devicesList = useMemo(() => {
    const list = resources.filter((r) => r.resourceType === CaseResourceType.OCSConsole);

    return list.map((r) => resourceById[CaseResourceType.OCSConsole].get(r.resourceId)).filter((r) => !!r);
  }, [resources, resourceById]);

  const specialistList = useMemo(() => {
    const list = resources.filter((r) => r.resourceType === CaseResourceType.ClinicalSpecialist);

    return list
      .map((r) => {
        return resourceById[CaseResourceType.ClinicalSpecialist].get(r.resourceId);
      })
      .filter((item) => !!item);
  }, [resources, resourceById]);
  const surgeonList = useMemo(() => {
    const list = resources.filter((r) => r.resourceType === CaseResourceType.Surgeon);

    return list
      .map((r) => {
        return resourceById[CaseResourceType.Surgeon].get(r.resourceId);
      })
      .filter((item) => !!item);
  }, [resources, resourceById]);
  const crewList = useMemo(() => {
    if (!isAccommodationLeg) return [];

    const list = resources.filter((r) => r.resourceType === CaseResourceType.Crew);

    return list
      .map((r) => {
        return resourceById[CaseResourceType.Crew].get(r.resourceId);
      })
      .filter((item): item is CrewLogisticsResourceType => !!item);
  }, [isAccommodationLeg, resources, resourceById]);

  return (
    <div className="flex gap-1 items-center flex-wrap">
      {hasScs && <ResourceAvatar key="SCS" id="SCS" type="scs" size="sm" radius="sm" />}
      {devicesList.map((device) => (
        <ResourceAvatar
          key={device.resourceId}
          id={device.resourceId}
          type="device"
          size="sm"
          radius="sm"
          organ={device.data?.organType}
          src={device.data?.logoPath}
        />
      ))}
      {specialistList.map((cs) => (
        <div key={cs.resourceId} className="flex gap-1 items-center">
          <TmdxAvatar type="clinical-specialist" size="sm" radius="sm" src={cs?.data?.profilePhoto} />
          <span className="text-s-r text-base-10">{getEmployeeName('CS', cs?.data)}</span>
        </div>
      ))}
      {surgeonList.map((s) => (
        <div key={s.resourceId} className="flex gap-1 items-center">
          <TmdxAvatar type="retrieval-surgeon" size="sm" radius="sm" src={s?.data?.profilePhoto} />
          <span className="text-s-r text-base-10">{getEmployeeName('S', s?.data)}</span>
        </div>
      ))}
      {crewList.map((pilot) => (
        <div key={pilot.resourceId} className="flex gap-1 items-center">
          <TmdxAvatar type="user" size="sm" radius="sm" />
          <span className="text-s-r text-base-10">{`${getFullName(pilot.firstName || '', pilot.lastName || '')} (TMDX Pilot)`}</span>
        </div>
      ))}
    </div>
  );
};
